/**
 * ProcessedDataChart Component
 * High-performance time series chart with LTTB downsampling
 * X-axis only zoom with synchronization support
 */

import React, { useRef, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import 'uplot/dist/uPlot.min.css';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/shared/components/ui/card';
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from '@/shared/components/feedback/Alert';
import { AlertCircle } from 'lucide-react';
import { ProcessedDataChartProps } from './types';
import { LOCALE, CHART_HEIGHT } from './constants';
import { useProcessedData, useDisplayData } from './hooks';
import { useChartInstance } from './hooks/useChartInstance';
import { ChartControls } from './components';

const ProcessedDataChart: React.FC<ProcessedDataChartProps> = ({
  data,
  seriesName,
  filename,
  sharedBrushRange,
  onSharedBrushChange,
}) => {
  const { t } = useTranslation('charts');
  const containerRef = useRef<HTMLDivElement>(null);
  const [showPointsEnabled, setShowPointsEnabled] = useState(false);

  // Refs for adaptive points plugin (bypass re-renders)
  const showPointsRef = useRef(false);
  const showPointsEnabledRef = useRef(false);
  showPointsEnabledRef.current = showPointsEnabled;

  // Process raw data into chart format
  const processedData = useProcessedData(data, seriesName);

  // Always show all data (no downsampling)
  const displayData = useDisplayData(processedData, true);

  // Initialize chart instance with sync support
  const { chartRef, handleResetZoom } = useChartInstance({
    containerRef,
    displayData,
    processedData,
    seriesName,
    sharedBrushRange,
    onSharedBrushChange,
    showPointsRef,
    showPointsEnabledRef,
  });

  // Toggle points handler - triggers redraw without resetting zoom
  const handleToggleShowPoints = useCallback(() => {
    setShowPointsEnabled(prev => {
      const newValue = !prev;
      showPointsEnabledRef.current = newValue;
      // Trigger redraw to update points visibility
      if (chartRef.current) {
        chartRef.current.redraw(false, false);
      }
      return newValue;
    });
  }, [chartRef]);

  // Format time for display
  const formatTime = (ts: number) => {
    const d = new Date(ts * 1000);
    return new Intl.DateTimeFormat(LOCALE, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).format(d);
  };

  // Early return if no data
  if (!data || data.length === 0 || processedData.timestamps.length === 0) {
    return (
      <Card className="w-full">
        <CardHeader>
          <CardTitle>
            {filename ? `${filename} - ${seriesName}` : seriesName}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>{t("showAllData.alertTitle", { ns: 'dataAdjustments' })}</AlertTitle>
            <AlertDescription>
              {t("showAllData.alertDescription", { ns: 'dataAdjustments' })}
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  const displayStart = processedData.timestamps[0];
  const displayEnd = processedData.timestamps[processedData.timestamps.length - 1];

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle>
          {filename ? `${filename} - ${seriesName}` : seriesName}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {/* Controls */}
        <ChartControls
          totalPoints={processedData.timestamps.length}
          onResetZoom={handleResetZoom}
          showPointsEnabled={showPointsEnabled}
          onToggleShowPoints={handleToggleShowPoints}
        />

        {/* Time range */}
        <div className="text-center text-xs sm:text-sm text-muted-foreground mb-1.5 sm:mb-2">
          {t("showAllData.timeRange", { ns: 'dataAdjustments', defaultValue: "Time Range" })}: {formatTime(displayStart)} – {formatTime(displayEnd)}
        </div>

        {/* Zoom instructions */}
        <div className="text-[10px] sm:text-xs text-muted-foreground text-center mb-1.5 sm:mb-2">
          {t("dataChart.zoomHint", "Scroll to zoom, drag to select area, double-click to reset")}
        </div>

        {/* Chart container */}
        <style>{`
          .u-select {
            background: rgba(59, 130, 246, 0.15) !important;
            border: 1px dashed rgba(59, 130, 246, 0.5) !important;
          }
          .u-cursor-x, .u-cursor-y {
            border-color: rgba(59, 130, 246, 0.4) !important;
          }
          .u-legend {
            position: relative !important;
            margin-top: 8px !important;
            font-size: 9px !important;
            flex-wrap: wrap !important;
            width: 100% !important;
            table-layout: fixed !important;
          }
          .u-legend th,
          .u-legend td {
            padding: 1px 2px !important;
            white-space: nowrap !important;
            overflow: hidden !important;
            text-overflow: ellipsis !important;
            max-width: 80px !important;
          }
          .u-legend .u-label {
            display: inline-block !important;
            max-width: 60px !important;
            overflow: hidden !important;
            text-overflow: ellipsis !important;
            vertical-align: middle !important;
          }
          .u-legend .u-marker {
            width: 8px !important;
            height: 8px !important;
            margin-right: 4px !important;
          }
          @media (min-width: 640px) {
            .u-legend {
              font-size: 12px !important;
            }
            .u-legend th,
            .u-legend td {
              padding: 2px 4px !important;
              max-width: 150px !important;
            }
            .u-legend .u-label {
              max-width: 120px !important;
            }
            .u-legend .u-marker {
              width: 12px !important;
              height: 12px !important;
            }
          }
        `}</style>
        <div
          ref={containerRef}
          className="w-full pb-8 sm:pb-12"
          style={{ height: CHART_HEIGHT }}
        />
      </CardContent>
    </Card>
  );
};

export default React.memo(ProcessedDataChart);
